import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/LanguageContext";

/**
 * ManagementTeamSection — visible to clients only after full KYC approval.
 * Falls back to a "locked" CTA pointing to the KYC page if the gate is closed.
 */
export default function ManagementTeamSection() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/team")
      .then(({ data }) => alive && setData(data))
      .catch(() => alive && setData({ eligible: false, members: [] }));
    return () => {
      alive = false;
    };
  }, []);

  if (!data) return null;

  return (
    <section
      className="mt-14 br-fade-up br-delay-5"
      data-testid="management-team-section"
    >
      <div className="br-overline" data-testid="team-overline">
        {t("team.overline")}
      </div>
      <h2 className="font-serif-display text-[2rem] mt-3" data-testid="team-title">
        {t("team.title")}
      </h2>

      {data.eligible ? (
        <>
          <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-3 max-w-[640px] leading-relaxed">
            {t("team.intro")}
          </p>
          <div
            className="mt-7 grid md:grid-cols-2 xl:grid-cols-3 gap-4"
            data-testid="team-grid"
          >
            {data.members.map((m) => (
              <TeamCard key={m.crd_number} m={m} t={t} />
            ))}
          </div>
        </>
      ) : (
        <TeamLockedCard t={t} />
      )}
    </section>
  );
}

function TeamCard({ m, t }) {
  return (
    <div
      className="br-card p-6 transition-transform duration-300 hover:-translate-y-0.5"
      data-testid={`team-card-${m.crd_number}`}
    >
      <div
        className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase"
        style={{ color: "var(--br-bronze)" }}
      >
        {m.role}
      </div>
      <h3 className="font-serif-display text-[1.35rem] mt-2 text-[color:var(--br-parchment)] leading-tight">
        {m.name}
      </h3>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[0.78rem]">
        <div>
          <div className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
            {t("team.crd")}
          </div>
          <div
            className="font-mono-fin mt-1"
            style={{ color: "var(--br-parchment)" }}
          >
            {m.crd_number}
          </div>
        </div>
        <div>
          <div className="font-mono-fin text-[0.6rem] tracking-[0.22em] uppercase text-[color:var(--br-text-dim)]">
            {t("team.years_experience")}
          </div>
          <div
            className="font-serif-display text-[1.4rem] mt-1"
            style={{ color: "var(--br-bronze)" }}
          >
            {m.years_experience}
          </div>
        </div>
      </div>

      <a
        href={m.finra_url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-block font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase border px-3 py-2 transition-colors"
        style={{ borderColor: "var(--br-bronze-soft)", color: "var(--br-bronze)" }}
        data-testid={`team-finra-${m.crd_number}`}
      >
        {t("team.finra_link")}
      </a>
    </div>
  );
}

function TeamLockedCard({ t }) {
  return (
    <div
      className="mt-7 br-card p-8 text-center"
      style={{ borderStyle: "dashed", borderColor: "var(--br-line-bright)" }}
      data-testid="team-locked-card"
    >
      <div
        className="font-mono-fin text-[0.66rem] tracking-[0.22em] uppercase"
        style={{ color: "var(--br-bronze)" }}
      >
        {t("team.locked_title")}
      </div>
      <p className="text-[0.95rem] text-[color:var(--br-text-dim)] mt-4 max-w-[560px] mx-auto leading-relaxed">
        {t("team.locked_body")}
      </p>
      <Link
        to="/kyc"
        className="br-btn-outline mt-6 inline-block"
        data-testid="team-locked-cta"
      >
        {t("nav.kyc")} →
      </Link>
    </div>
  );
}
